/**
 * MessageMediaInfoRemapper
 *
 * Round-trips a community-message `mediaInfo` nested object between its
 * Firestore wire JSON and a MessageMediaInfoDto.
 *
 * Flutter source: lib/data/models/message_media_info.dart:6-34
 *   (+ generated message_media_info.g.dart:9-21).
 *
 * `@JsonKey` overrides (message_media_info.dart:10-13): the Dart properties
 * `relativePath` / `relativeThumbnailPath` are persisted under the Firestore
 * keys `path` / `thumbnail`. The DTO field name IS the Firestore field, so the
 * remapper reads/writes the wire keys `path` / `thumbnail` straight into the
 * DTO's `path` / `thumbnail` fields, carrying the RELATIVE (non-bucket-prefixed)
 * value verbatim. The Dart `get path` / `get thumbnail` bucket-prefix getters
 * are an entity-layer concern (Task 4) and are NOT applied here.
 *
 * `isUploading` is `late bool` (required); `fromJson` applies a `?? false`
 * defensive default. `aspectRatio` / `path` / `thumbnail` are optional and pass
 * through as `undefined` when absent.
 */
import type { MessageMediaInfoDto } from '@/data/dtos/info/messageMediaInfoDto';
import type { JsonObject } from '@/data/types/json';
import type { MessageMediaInfoEntity } from '@/domain/entities/info/messageMediaInfoEntity';
import { MediaUtils } from '@/domain/utils/mediaUtils';

export class MessageMediaInfoRemapper {
  static fromJson(data: JsonObject): MessageMediaInfoDto {
    const dto: MessageMediaInfoDto = {
      isUploading: (data['isUploading'] as boolean | undefined) ?? false,
    };

    if (data['aspectRatio'] !== undefined) {
      dto.aspectRatio = data['aspectRatio'] as number | undefined;
    }
    // JsonKey overrides: Firestore stores `path` / `thumbnail`; the DTO mirrors
    // those wire keys, holding the relative (non-bucket-prefixed) value.
    if (data['path'] !== undefined) {
      dto.path = data['path'] as string | undefined;
    }
    if (data['thumbnail'] !== undefined) {
      dto.thumbnail = data['thumbnail'] as string | undefined;
    }

    return dto;
  }

  static fromJsonList(dataList: JsonObject[]): MessageMediaInfoDto[] {
    return dataList.map(MessageMediaInfoRemapper.fromJson);
  }

  static toJson(dto: MessageMediaInfoDto): JsonObject {
    const json: JsonObject = {
      isUploading: dto.isUploading,
    };

    if (dto.aspectRatio !== undefined) json.aspectRatio = dto.aspectRatio;
    // Emit the Flutter wire keys `path` / `thumbnail` (NOT the Dart property
    // names `relativePath` / `relativeThumbnailPath`).
    if (dto.path !== undefined) json.path = dto.path;
    if (dto.thumbnail !== undefined) json.thumbnail = dto.thumbnail;

    return json;
  }

  /**
   * Map a DTO to its domain entity, deriving the absolute media/thumbnail
   * download URLs from the relative DTO paths. The DTO's `path` / `thumbnail`
   * hold the RELATIVE (non-bucket-prefixed) value; the entity carries both the
   * relative values and the derived absolute URLs (parity with Flutter's
   * `relativePath` + `get path` getter, message_media_info.dart:15-27).
   */
  static toEntity(dto: MessageMediaInfoDto): MessageMediaInfoEntity {
    return {
      isUploading: dto.isUploading,
      aspectRatio: dto.aspectRatio,
      relativePath: dto.path,
      relativeThumbnailPath: dto.thumbnail,
      // Absolute URLs: derived only when the relative value is non-null
      // (parity with Flutter's `get path` returning null when relativePath ==
      // null — message_media_info.dart:16-19).
      path: MessageMediaInfoRemapper.toAbsoluteUrl(dto.path),
      thumbnail: MessageMediaInfoRemapper.toAbsoluteUrl(dto.thumbnail),
    };
  }

  /**
   * Map an entity back to its DTO for serialization. Only the RELATIVE values
   * are persisted (the absolute getters are derived, never stored — parity with
   * Flutter where `get path` is a computed getter and only `relativePath` is
   * serialized via `@JsonKey(name: 'path')`). Named `toDtoFromEntity` (not
   * `toDto`) to avoid colliding with the JSON-emitting `toJson`.
   */
  static toDtoFromEntity(entity: MessageMediaInfoEntity): MessageMediaInfoDto {
    const dto: MessageMediaInfoDto = {
      isUploading: entity.isUploading,
    };
    if (entity.aspectRatio !== undefined) dto.aspectRatio = entity.aspectRatio;
    // Persist the RELATIVE values under the wire keys `path` / `thumbnail`
    // (the absolute URLs are derived, never stored).
    if (entity.relativePath !== undefined) dto.path = entity.relativePath;
    if (entity.relativeThumbnailPath !== undefined) dto.thumbnail = entity.relativeThumbnailPath;
    return dto;
  }

  /**
   * Prepend the default bucket prefix to a relative path exactly once, or
   * return `undefined` when the relative value is null/absent (parity with the
   * Flutter `get path` getters returning `null` for a `null` relative path).
   */
  private static toAbsoluteUrl(relativePath: string | undefined): string | undefined {
    if (relativePath == null) {
      return undefined;
    }
    return MediaUtils.joinDefaultBucketUrl(relativePath);
  }
}
